import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';

/**
 * Quotation With GST Template - A4 Size
 * Standard professional quotation template matching the exact reference layout:
 * - Bold Red Title: "• ——— QUOTATION ——— •"
 * - Top Box: Business Logo & Details (Left) + Quotation Details (Right)
 * - Middle Box: Customer Details (Left) + Terms & Conditions from Client API (Right)
 * - Items Table: 12 Columns (S.No, Description, HSN/SAC, Qty, Unit, Rate, Amount, Discount, Taxable Amount, Tax %, Tax Amt, Total Amount)
 * - Totals Box: Amount in Words (Left) + Tax & Grand Total (Right)
 * - Bank Details & Signatory Box: Bank Info from Client API (Left) + Signature / Authorized Signatory (Right)
 * - Footer Banner: "Thank You For Your Business!"
 */
export class QuotationWithGstTemplate extends BasePdfTemplate {
  override templateName = 'quotation-with-gst';
  override templateVersion = '1.0.0';
  override description = 'Professional Quotation template with GST breakdown';
  override pageSize: 'A3' | 'A4' | 'A5' | 'LETTER' | 'THERMAL' = 'A4';

  /**
   * Converts numbers into Indian Currency Words (e.g. "Rupees Seven Lakh Thirty Seven Thousand Forty Eight Only.")
   */
  private numberToIndianWords(num: number): string {
    if (!num || isNaN(num) || num === 0) return 'Rupees Zero Only.';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = [
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];

    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };

    const whole = Math.floor(Math.abs(num));
    const decimal = Math.round((Math.abs(num) - whole) * 100);

    const getIndianWords = (n: number): string => {
      if (n === 0) return '';
      const crore = Math.floor(n / 10000000);
      const lakh = Math.floor((n % 10000000) / 100000);
      const thousand = Math.floor((n % 100000) / 1000);
      const remainder = n % 1000;

      let res = '';
      if (crore > 0) res += getIndianWords(crore) + ' Crore ';
      if (lakh > 0) res += convertLessThanThousand(lakh) + ' Lakh ';
      if (thousand > 0) res += convertLessThanThousand(thousand) + ' Thousand ';
      if (remainder > 0) res += convertLessThanThousand(remainder) + ' ';

      return res.trim();
    };

    let words = 'Rupees ' + getIndianWords(whole);
    if (decimal > 0) {
      words += ' and ' + convertLessThanThousand(decimal) + ' Paise';
    }
    words += ' Only.';
    return words;
  }

  /**
   * Formats dates as DD-MM-YYYY
   */
  private formatIndianDate(date: Date | string | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return String(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /**
   * Formats numbers to Indian Currency string (e.g. ₹7,01,950.00)
   */
  private formatInr(amount: number): string {
    if (isNaN(amount)) return '₹0.00';
    return '₹' + amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * pdfMake requires image to be a Base64 data URL (e.g. data:image/png;base64,...).
   * Web URLs (http/https) are rejected by pdfMake's VFS. This check prevents runtime crashes.
   */
  private isValidImage(str: string | undefined | null): boolean {
    if (!str || typeof str !== 'string') return false;
    const trimmed = str.trim();
    return trimmed.startsWith('data:image/');
  }

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    // ── 1. Client & Business Info from API & LocalStorage ───────────────────
    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    const clientSaleData = JSON.parse(localStorage.getItem('client_sale_data') || '{}');

    // Resolve pageSize from clientSaleData printformat
    const rawFormat = clientSaleData?.printformat || 'A4';
    const format = String(rawFormat).toUpperCase().trim();
    const isA5 = format.toUpperCase() === 'A5';
    // const resolvedPageSize: any = (['A3', 'A4', 'A5', 'LETTER'].includes(format)) ? format : 'A4';
    const isThermal = format === 'THERMAL' || format === '3INCH';

    const businessName =
      clientSaleData?.client_name ||
      storename?.name ||
      data.businessName ||
      data.business?.name ||
      'Shendhuran Tex';

    const businessAddress = [
      storename?.address || clientSaleData?.address,
      storename?.address1 || clientSaleData?.address2,
      storename?.address2,
      storename?.city,
      storename?.state || clientSaleData?.state_name,
      storename?.pincode || clientSaleData?.pincode,
    ]
      .filter((p: any) => p && String(p).trim())
      .join(', ') || data.businessAddress || data.business?.address || '';

    const businessPhone =
      clientSaleData?.mobile ||
      storename?.mobile ||
      storename?.phone ||
      data.businessPhone ||
      data.business?.phone ||
      '';

    const businessGSTIN =
      clientSaleData?.gstno ||
      storename?.gstin ||
      storename?.gst_number ||
      data.businessGSTIN ||
      data.business?.gstin ||
      '';

    const businessLogo =
      clientSaleData?.logo ||
      data.businessLogo ||
      storename?.logo ||
      storename?.image ||
      '';

    const signatureImage =
      clientSaleData?.signature ||
      data.signature ||
      data.signatureImage ||
      '';

    // ── 2. Quotation Info ─────────────────────────────────────────────────
    const quotationNo =
      data.quotationnumber ||
      data.quotationNumber ||
      data.invoiceNumber ||
      data.referenceNo ||
      'QT-' + new Date().getFullYear();
    const quotationDate = this.formatIndianDate(data.invoiceDate || data.invoicedate || new Date());
    const validTill = this.formatIndianDate(
      data.quotationValidityDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    );

    // ── 3. Customer Info ──────────────────────────────────────────────────
    const customerName = data.customerName || data.party?.name || 'Walk-In Customer';
    const customerAddress = this.getCustomerAddress(data) || data.party?.address || '';
    const customerPhone = data.customerMobile || data.party?.mobile || data.party?.phone || '';
    const customerGSTIN = data.customerGSTIN || data.party?.gstin || '-';

    // ── 4. Bank Details from Client API ───────────────────────────────────
    const rawBank = clientSaleData?.bankaccount || data.bankDetails;
    const clientBank = Array.isArray(rawBank) ? rawBank[0] : rawBank;

    const bankName =
      clientBank?.bank_name ||
      clientBank?.bankname ||
      clientBank?.bankName ||
      storename?.bank_name ||
      'Indian Bank';

    const accountName =
      clientBank?.account_name ||
      clientBank?.account_holder_name ||
      clientBank?.accountHolderName ||
      storename?.account_name ||
      businessName.toUpperCase();

    const accountNo =
      clientBank?.account_no ||
      clientBank?.account_number ||
      clientBank?.accountNumber ||
      storename?.account_no ||
      '7684920654';

    const ifscCode =
      clientBank?.ifsc_code ||
      clientBank?.ifsc ||
      clientBank?.ifscCode ||
      storename?.ifsc_code ||
      'IDIB000P106';

    const branchName =
      clientBank?.branch ||
      clientBank?.branch_name ||
      clientBank?.branchName ||
      storename?.branch ||
      storename?.city ||
      'Erode';

    // ── 5. Terms & Conditions from Client API ─────────────────────────────
    const rawTerms = clientSaleData?.terms || data.termsAndConditions || data.terms;
    let termsParagraphs: any[] = [];
    if (rawTerms && typeof rawTerms === 'string' && rawTerms.trim()) {
      const lines = rawTerms
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);

      termsParagraphs = lines.map((line: string, idx: number) => ({
        text: line.match(/^\d+\./) ? line : `${idx + 1}. ${line}`,
        fontSize: 7,
        color: '#334155',
        margin: [0, 0, 0, 1] as [number, number, number, number],
      }));
    } else {
      termsParagraphs = [
        { text: `1. This quotation is valid until ${validTill}.`, fontSize: 7, color: '#334155', margin: [0, 0, 0, 1] },
        { text: '2. Goods once sold will not be taken back.', fontSize: 7, color: '#334155', margin: [0, 0, 0, 1] },
        { text: '3. Payment to be made before dispatch.', fontSize: 7, color: '#334155', margin: [0, 0, 0, 1] },
        { text: '4. Transport charges extra as applicable.', fontSize: 7, color: '#334155', margin: [0, 0, 0, 0] },
      ];
    }

    // ── 6. Items Processing & Pagination (25 items per page) ──────────────
    const ITEMS_PER_PAGE = isA5 ? 10 : 25;;
    const rawItems = data.items || [];
    let totalTaxableValue = 0;
    let totalTaxAmount = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    let maxTaxRate = 0;

    const isInterState =
      (data.igst ?? 0) > 0 && (data.cgst ?? 0) === 0 && (data.sgst ?? 0) === 0;

    // Calculate totals across ALL items first
    rawItems.forEach((item) => {
      const qty = item.quantity || 1;
      const rate = item.price || 0;
      const amount = qty * rate;
      const discount = item.discount || item.discountamt || item.discountAmount || 0;
      const taxableAmount = item.taxableAmount || (amount - discount);
      const taxRate = Number(item.taxPercent || item.taxRate || item.tax || 0);
      if (taxRate > maxTaxRate) maxTaxRate = taxRate;

      const taxAmount = item.taxamount || item.taxAmount || ((taxableAmount * taxRate) / 100);

      totalTaxableValue += taxableAmount;
      totalTaxAmount += taxAmount;

      if (isInterState) {
        totalIgst += taxAmount;
      } else {
        totalCgst += taxAmount / 2;
        totalSgst += taxAmount / 2;
      }
    });

    const grandTotal = data.grandTotal ?? data.totalAmount ?? (totalTaxableValue + totalTaxAmount);
    const amountInWords = this.numberToIndianWords(Math.round(grandTotal));

    // Determine CGST / SGST rates display (e.g. 2.5% if total tax is 5%)
    const cgstRateDisplay = maxTaxRate > 0 ? `${(maxTaxRate / 2).toFixed(1).replace('.0', '')}%` : '2.5%';
    const sgstRateDisplay = cgstRateDisplay;
    const igstRateDisplay = maxTaxRate > 0 ? `${maxTaxRate.toFixed(1).replace('.0', '')}%` : '5%';

    const finalCgst = data.cgst !== undefined ? data.cgst : totalCgst;
    const finalSgst = data.sgst !== undefined ? data.sgst : totalSgst;
    const finalIgst = data.igst !== undefined ? data.igst : totalIgst;

    const hasValidLogo = this.isValidImage(businessLogo);
    const hasValidSignature = this.isValidImage(signatureImage);

    // Divide items into page chunks of max 25 items each
    const chunks: any[][] = [];
    if (rawItems.length === 0) {
      chunks.push([]);
    } else {
      for (let i = 0; i < rawItems.length; i += ITEMS_PER_PAGE) {
        chunks.push(rawItems.slice(i, i + ITEMS_PER_PAGE));
      }
    }
    const totalPages = chunks.length;
    // ── 7. THERMAL RECEIPT FORMAT (3INCH / POS) ───────────────────────────
    if (isThermal) {
      const PAGE_WIDTH_THERMAL = 226;

      const businessDetails: any[] = [
        ...(hasValidLogo
          ? [
            {
              image: businessLogo,
              width: 50,
              height: 35,
              fit: [50, 40] as [number, number],
              alignment: 'center',
              margin: [0, 0, 0, 3] as [number, number, number, number],
            },
          ]
          : []),
        { text: businessName, fontSize: 10, bold: true, alignment: 'center' },
        ...(businessAddress ? [{ text: businessAddress, fontSize: 7, alignment: 'center', margin: [0, 1, 0, 1] as [number, number, number, number] }] : []),
        ...(businessPhone ? [{ text: `Ph: ${businessPhone}`, fontSize: 7, alignment: 'center' }] : []),
        ...(businessGSTIN ? [{ text: `GSTIN: ${businessGSTIN}`, fontSize: 7, bold: true, alignment: 'center', margin: [0, 1, 0, 2] as [number, number, number, number] }] : []),
        { text: '•  QUOTATION  •', alignment: 'center', fontSize: 9.5, bold: true, color: '#b91c1c', margin: [0, 3, 0, 3] as [number, number, number, number] },
      ];

      const quotationAndCustomer: any = {
        table: {
          widths: ['50%', '50%'],
          body: [
            [
              { text: `Quote No: ${quotationNo}`, bold: true },
              { text: `Date: ${quotationDate}`, alignment: 'right' },
            ],
            [
              { text: `Valid Till: ${validTill}`, bold: true },
              { text: `Customer: ${customerName}`, alignment: 'right', bold: true },
            ],
            ...(customerPhone
              ? [
                [
                  { text: `Ph: ${customerPhone}`, colSpan: 2 },
                  {},
                ],
              ]
              : []),
          ],
        },
        layout: 'noBorders',
        fontSize: 6.5,
        margin: [0, 2, 0, 4] as [number, number, number, number],
      };

      const tableBody: any[][] = [
        [
          { text: '#', bold: true, border: [false, true, false, true], fontSize: 6.5 },
          { text: 'Item', bold: true, border: [false, true, false, true], fontSize: 6.5 },
          { text: 'Qty', alignment: 'center', bold: true, border: [false, true, false, true], fontSize: 6.5 },
          { text: 'Rate', alignment: 'right', bold: true, border: [false, true, false, true], fontSize: 6.5 },
          { text: 'Total', alignment: 'right', bold: true, border: [false, true, false, true], fontSize: 6.5 },
        ],
      ];

      rawItems.forEach((item: any, index: number) => {
        const qty = item.quantity || 1;
        const rate = item.price || 0;
        const discount = item.discount || item.discountamt || item.discountAmount || 0;
        const taxableAmount = item.taxableAmount || (qty * rate - discount);
        const taxRate = Number(item.taxPercent || item.taxRate || item.tax || 0);
        const taxAmount = item.taxamount || item.taxAmount || ((taxableAmount * taxRate) / 100);
        const totalAmount = item.total || (taxableAmount + taxAmount);

        tableBody.push([
          { text: String(index + 1), border: [false, false, false, false], fontSize: 6.5 },
          {
            stack: [
              { text: `${item.name || '-'}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, alignment: 'left', bold: true, fontSize: 6.5 },
              ...(taxRate > 0 || discount > 0
                ? [
                  {
                    text: `${taxRate > 0 ? `Tax ${taxRate}%` : ''}${discount > 0 ? ` | Disc ₹${discount}` : ''}`,
                    fontSize: 5.5,
                    color: '#475569',
                  },
                ]
                : []),
            ],
            border: [false, false, false, false],
          },
          { text: String(qty), alignment: 'center', border: [false, false, false, false], fontSize: 6.5 },
          { text: rate.toFixed(2), alignment: 'right', border: [false, false, false, false], fontSize: 6.5 },
          { text: Number(item.is_free) === 1 ? '0.00' : totalAmount.toFixed(2), alignment: 'right', bold: true, border: [false, false, false, false], fontSize: 6.5 },
        ]);
      });

      const thermalTable: any = {
        table: {
          headerRows: 1,
          widths: [12, '*', 20, 28, 38],
          body: tableBody,
        },
        layout: {
          defaultBorder: false,
          paddingTop: () => 1.5,
          paddingBottom: () => 1.5,
          paddingLeft: () => 1,
          paddingRight: () => 1,
        },
        fontSize: 6.5,
        margin: [0, 0, 0, 3] as [number, number, number, number],
      };

      const summaryBody: any[][] = [
        [
          { text: `Items: ${rawItems.length}`, border: [false, true, false, false], fontSize: 6.5, margin: [0, 2, 0, 1] },
          { text: 'Taxable Value', alignment: 'right', border: [false, true, false, false], fontSize: 6.5, margin: [0, 2, 0, 1] },
          { text: totalTaxableValue.toFixed(2), alignment: 'right', bold: true, border: [false, true, false, false], fontSize: 6.5, margin: [0, 2, 0, 1] },
        ],
      ];

      if (isInterState && totalIgst > 0) {
        summaryBody.push([
          { text: '', border: [false, false, false, false] },
          { text: `IGST @ ${igstRateDisplay}`, alignment: 'right', border: [false, false, false, false], fontSize: 6.5 },
          { text: totalIgst.toFixed(2), alignment: 'right', border: [false, false, false, false], fontSize: 6.5 },
        ]);
      } else {
        if (totalCgst > 0) {
          summaryBody.push([
            { text: '', border: [false, false, false, false] },
            { text: `CGST @ ${cgstRateDisplay}`, alignment: 'right', border: [false, false, false, false], fontSize: 6.5 },
            { text: totalCgst.toFixed(2), alignment: 'right', border: [false, false, false, false], fontSize: 6.5 },
          ]);
        }
        if (totalSgst > 0) {
          summaryBody.push([
            { text: '', border: [false, false, false, false] },
            { text: `SGST @ ${sgstRateDisplay}`, alignment: 'right', border: [false, false, false, false], fontSize: 6.5 },
            { text: totalSgst.toFixed(2), alignment: 'right', border: [false, false, false, false], fontSize: 6.5 },
          ]);
        }
      }

      summaryBody.push([
        { text: 'Grand Total', bold: true, alignment: 'right', border: [false, true, false, true], margin: [0, 3, 4, 3], fontSize: 9.5, colSpan: 2 },
        {},
        { text: this.formatInr(grandTotal), bold: true, alignment: 'right', border: [false, true, false, true], margin: [0, 3, 0, 3], fontSize: 9.5 },
      ]);

      const summaryTable: any = {
        table: {
          widths: [45, '*', 'auto'],
          body: summaryBody,
        },
        layout: {
          defaultBorder: false,
          paddingTop: () => 1,
          paddingBottom: () => 1,
          paddingLeft: () => 1,
          paddingRight: () => 1,
        },
        fontSize: 6.5,
        margin: [0, 2, 0, 4] as [number, number, number, number],
      };

      const footer: any[] = [
        { text: `Amount: ${amountInWords}`, fontSize: 6.5, alignment: 'center', margin: [0, 2, 0, 4] as [number, number, number, number] },
        ...(bankName
          ? [
            {
              text: `Bank: ${bankName} | A/c: ${accountNo} | IFSC: ${ifscCode}`,
              fontSize: 6,
              alignment: 'center',
              margin: [0, 1, 0, 3] as [number, number, number, number],
            },
          ]
          : []),
        { text: 'Thank You For Your Business!', fontSize: 8, bold: true, alignment: 'center', margin: [0, 2, 0, 2] as [number, number, number, number] },
        { text: '********************************', fontSize: 7, alignment: 'center', margin: [0, 2, 0, 8] as [number, number, number, number] },
      ];

      return {
        pageSize: { width: PAGE_WIDTH_THERMAL, height: 'auto' },
        pageMargins: [8, 8, 8, 12],
        defaultStyle: {
          font: 'NotoSansTamil',
          fontSize: 6.5,
          color: '#000000',
        },
        content: [
          ...businessDetails,
          quotationAndCustomer,
          thermalTable,
          summaryTable,
          ...footer,
        ],
      };
    }

    // ── 7. Build Document Content across Pages ────────────────────────────
    const docContent: any[] = [];

    chunks.forEach((chunk, pageIdx) => {
      // 1. Page Break & Top Headers
      if (pageIdx === 0) {
        // First Page: Full Header & Top Boxes
        docContent.push(
          // Header Title
          {
            text: '• ———  QUOTATION  ——— •',
            fontSize: 16,
            bold: true,
            color: '#b91c1c',
            alignment: 'center',
            margin: [0, 0, 0, 8],
          },
          // Main Box 1: Business Details & Quotation Meta
          {
            table: {
              widths: ['100%'],
              body: [
                [
                  // Left: Business Info with Logo
                  {
                    columns: [
                      ...(hasValidLogo
                        ? [
                          {
                            image: businessLogo,
                            width: 44,
                            height: 38,
                            fit: [45, 40] as [number, number],
                            margin: [0, 0, 6, 0] as [number, number, number, number],
                          },
                        ]
                        : []),
                      {
                        stack: [
                          { text: businessName, fontSize: 11, bold: true, color: '#0f172a', margin: [0, 0, 0, 2] },
                          ...(businessAddress
                            ? [{ text: businessAddress, fontSize: 7.5, color: '#334155', margin: [0, 0, 0, 1] as [number, number, number, number] }]
                            : []),
                          ...(businessPhone
                            ? [{ text: `Ph: ${businessPhone}`, fontSize: 7.5, color: '#334155', margin: [0, 0, 0, 1] as [number, number, number, number] }]
                            : []),
                          ...(businessGSTIN
                            ? [{ text: `GSTIN: ${businessGSTIN}`, fontSize: 7.5, bold: true, color: '#0f172a', margin: [0, 0, 0, 0] as [number, number, number, number] }]
                            : []),
                        ],
                        width: '*',
                      },
                    ],
                    margin: [4, 4, 4, 4],
                  },

                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => '#000000',
              vLineColor: () => '#000000',
            },
            margin: [0, 0, 0, 0],
          },
          // Main Box 2: Customer Details
          {
            table: {
              widths: ['50%', '50%'],
              body: [
                [
                  // Customer Details
                  {
                    stack: [
                      { text: 'To,', fontSize: 8, bold: true, margin: [0, 0, 0, 2] },
                      { text: customerName, fontSize: 8.5, bold: true, margin: [0, 0, 0, 2] },
                      ...(customerAddress
                        ? [{ text: customerAddress, fontSize: 7.5, color: '#334155', margin: [0, 0, 0, 1] as [number, number, number, number] }]
                        : []),
                      {
                        text: `Phone: ${customerPhone || '-'}    |    GSTIN: ${customerGSTIN || '-'}`,
                        fontSize: 7.5,
                        color: '#334155',
                        margin: [0, 0, 0, 0],
                      },
                    ],
                    margin: [4, 4, 4, 4],
                  },
                  // Right: Quotation Meta
                  {
                    table: {
                      widths: ['auto', 'auto', '*'],
                      body: [
                        [
                          { text: 'Quotation No', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                          { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                          { text: quotationNo, bold: true, fontSize: 7.5, border: [false, false, false, false] },
                        ],
                        [
                          { text: 'Date', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                          { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                          { text: quotationDate, fontSize: 7.5, border: [false, false, false, false] },
                        ],
                        [
                          { text: 'Valid Till', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                          { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                          { text: validTill, fontSize: 7.5, border: [false, false, false, false] },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                    margin: [4, 4, 4, 4],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => '#000000',
              vLineColor: () => '#000000',
            },
            margin: [0, 0, 0, 0],
          },
        );
      } else {
        // Subsequent Pages: Page Break + Clean Continuation Header
        docContent.push({
          text: '',
          pageBreak: 'before',
        });
        docContent.push({
          stack: [
            {
              text: '• ———  QUOTATION  ——— •',
              fontSize: 13,
              bold: true,
              color: '#b91c1c',
              alignment: 'center',
              margin: [0, 0, 0, 4],
            },
            {
              columns: [
                { text: `Quotation No : ${quotationNo}`, fontSize: 7.5, bold: true, color: '#0f172a' },
                { text: `Date : ${quotationDate}`, fontSize: 7.5, alignment: 'center', color: '#475569' },
                { text: `Page ${pageIdx + 1} of ${totalPages}`, fontSize: 7.5, alignment: 'right', bold: true, color: '#0f172a' },
              ],
              margin: [0, 0, 0, 6],
            },
          ],
        });
      }

      // 2. Main Box 3: 12-Column Items Table (Repeats Header on Every Page)
      const pageRows = chunk.map((item, idx) => {
        const globalIdx = pageIdx * ITEMS_PER_PAGE + idx;
        const qty = item.quantity || 1;
        const rate = item.price || 0;
        const amount = qty * rate;
        const discount = item.discount || item.discountamt || item.discountAmount || 0;
        const taxableAmount = item.taxableAmount || (amount - discount);
        const taxRate = Number(item.taxPercent || item.taxRate || item.tax || 0);
        const taxAmount = item.taxamount || item.taxAmount || ((taxableAmount * taxRate) / 100);
        const totalAmount = item.total || (taxableAmount + taxAmount);

        return [
          { text: String(globalIdx + 1), alignment: 'center', fontSize: 7, margin: [0, 3, 0, 3] },
          { text: item.name || '', alignment: 'left', bold: true, fontSize: 7, margin: [2, 3, 2, 3] },
          { text: (item.hsncode || item.hsn || '-').toString().substring(0, 8), alignment: 'center', fontSize: 7, margin: [0, 3, 0, 3] },
          { text: String(qty), alignment: 'center', fontSize: 7, margin: [0, 3, 0, 3] },
          { text: item.unit_mappings?.[0]?.unitdetails?.displayname || 'PCS', alignment: 'center', fontSize: 7, margin: [0, 3, 0, 3] },
          { text: this.formatInr(rate), alignment: 'right', fontSize: 7, margin: [0, 3, 1, 3] },
          { text: this.formatInr(Number(item.is_free) === 1 ? 0 : amount), alignment: 'right', fontSize: 7, margin: [0, 3, 1, 3] },
          { text: discount > 0 ? this.formatInr(discount) : '-', alignment: 'center', fontSize: 7, margin: [0, 3, 0, 3] },
          { text: this.formatInr(taxableAmount), alignment: 'right', fontSize: 7, margin: [0, 3, 1, 3] },
          { text: taxRate > 0 ? `${taxRate}%` : '0%', alignment: 'center', fontSize: 7, margin: [0, 3, 0, 3] },
          { text: this.formatInr(taxAmount), alignment: 'right', fontSize: 7, margin: [0, 3, 1, 3] },
          { text: this.formatInr(Number(item.is_free) === 1 ? 0 : totalAmount), alignment: 'right', bold: true, fontSize: 7, margin: [0, 3, 1, 3] },
        ];
      });

      docContent.push({
        table: {
          headerRows: 1,
          widths: [18, '*', 35, 20, 24, 36, 40, 34, 44, 20, 36, 48],
          body: [
            // Header Row (Repeated on every page)
            [
              { text: 'S.No', bold: true, alignment: 'center', fontSize: 7, margin: [0, 4, 0, 4] },
              { text: 'Description', bold: true, alignment: 'center', fontSize: 7, margin: [0, 4, 0, 4] },
              { text: 'HSN/\nSAC', bold: true, alignment: 'center', fontSize: 6.5, margin: [0, 2, 0, 2] },
              { text: 'Qty', bold: true, alignment: 'center', fontSize: 7, margin: [0, 4, 0, 4] },
              { text: 'Unit', bold: true, alignment: 'center', fontSize: 7, margin: [0, 4, 0, 4] },
              { text: 'Rate', bold: true, alignment: 'center', fontSize: 7, margin: [0, 4, 0, 4] },
              { text: 'Amount', bold: true, alignment: 'center', fontSize: 6.5, margin: [0, 2, 0, 2] },
              { text: 'Discount', bold: true, alignment: 'center', fontSize: 6.5, margin: [0, 2, 0, 2] },
              { text: 'Taxable\nAmount', bold: true, alignment: 'center', fontSize: 6.5, margin: [0, 2, 0, 2] },
              { text: 'Tax\n%', bold: true, alignment: 'center', fontSize: 6.5, margin: [0, 2, 0, 2] },
              { text: 'Tax Amt', bold: true, alignment: 'center', fontSize: 6.5, margin: [0, 2, 0, 2] },
              { text: 'Total\nAmount', bold: true, alignment: 'center', fontSize: 6.5, margin: [0, 2, 0, 2] },
            ],
            ...pageRows,
          ],
        },
        layout: {
          hLineWidth: () => 0.75,
          vLineWidth: () => 0.75,
          hLineColor: () => '#000000',
          vLineColor: () => '#000000',
          paddingLeft: () => 2,
          paddingRight: () => 2,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
        margin: [0, 0, 0, 0],
      });

      // 3. Footer Sections (ONLY on the LAST Page)
      if (pageIdx === totalPages - 1) {
        docContent.push(
          // Main Box 4: Amount in Words & Totals
          {
            table: {
              widths: ['52%', '48%'],
              body: [
                [
                  // Left: Amount in Words
                  {
                    stack: [
                      { text: 'Amount in Words:', fontSize: 7.5, bold: true, margin: [0, 0, 0, 2] },
                      { text: amountInWords, fontSize: 7.5, color: '#1e293b', margin: [0, 0, 0, 0] },
                    ],
                    margin: [4, 4, 4, 4],
                  },
                  // Right: Totals Breakdown Table
                  {
                    table: {
                      widths: ['*', 'auto'],
                      body: [
                        [
                          { text: 'Total Taxable Value', fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 4, 2] },
                          { text: this.formatInr(totalTaxableValue), alignment: 'right', fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 0, 2] },
                        ],
                        ...(isInterState
                          ? [
                            [
                              { text: `Total IGST @ ${igstRateDisplay}`, fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 4, 2] },
                              { text: this.formatInr(finalIgst), alignment: 'right', fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 0, 2] },
                            ],
                          ]
                          : [
                            [
                              { text: `Total CGST @ ${cgstRateDisplay}`, fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 4, 2] },
                              { text: this.formatInr(finalCgst), alignment: 'right', fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 0, 2] },
                            ],
                            [
                              { text: `Total SGST @ ${sgstRateDisplay}`, fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 4, 2] },
                              { text: this.formatInr(finalSgst), alignment: 'right', fontSize: 7.5, bold: true, border: [false, false, false, true], margin: [0, 2, 0, 2] },
                            ],
                          ]),
                        [
                          { text: 'GRAND TOTAL', fontSize: 8.5, bold: true, border: [false, false, false, false], margin: [0, 3, 4, 2] },
                          { text: this.formatInr(grandTotal), alignment: 'right', fontSize: 8.5, bold: true, border: [false, false, false, false], margin: [0, 3, 0, 2] },
                        ],
                      ],
                    },
                    layout: {
                      hLineWidth: () => 0.5,
                      vLineWidth: () => 0,
                      hLineColor: () => '#000000',
                    },
                    margin: [0, 0, 0, 0],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => '#000000',
              vLineColor: () => '#000000',
            },
            margin: [0, 0, 0, 0],
          },
          // Main Box 5: Bank Details & Authorized Signatory
          {
            table: {
              widths: ['60%', '40%'],
              body: [
                [
                  // Left: Bank Details from Client API
                  {
                    stack: [
                      { text: 'Bank Details:', fontSize: 8, bold: true, decoration: 'underline', margin: [0, 0, 0, 4] },
                      {
                        table: {
                          widths: ['auto', 'auto', '*'],
                          body: [
                            [
                              { text: 'A/c Name', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: accountName, fontSize: 7.5, border: [false, false, false, false] },
                            ],
                            [
                              { text: 'Bank Name', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: bankName, fontSize: 7.5, border: [false, false, false, false] },
                            ],
                            [
                              { text: 'A/c No.', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: accountNo, fontSize: 7.5, border: [false, false, false, false] },
                            ],
                            [
                              { text: 'IFSC Code', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: ifscCode, fontSize: 7.5, border: [false, false, false, false] },
                            ],
                            [
                              { text: 'Branch', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: ':', bold: true, fontSize: 7.5, border: [false, false, false, false] },
                              { text: branchName, fontSize: 7.5, border: [false, false, false, false] },
                            ],
                          ],
                        },
                        layout: 'noBorders',
                      },
                    ],
                    margin: [4, 4, 4, 4],
                  },
                  // Right: Signature & Signatory
                  {
                    stack: [
                      { text: `For ${businessName.toUpperCase()}`, fontSize: 8, bold: true, alignment: 'right', margin: [0, 4, 4, 0] },
                      ...(hasValidSignature
                        ? [
                          {
                            image: signatureImage,
                            width: 70,
                            height: 25,
                            fit: [80, 30] as [number, number],
                            alignment: 'right',
                            margin: [0, 2, 4, 2] as [number, number, number, number],
                          },
                        ]
                        : [{ text: '', margin: [0, 20, 0, 0] as [number, number, number, number] }]),
                      { text: 'Authorized Signatory', fontSize: 7.5, bold: true, alignment: 'right', margin: [0, 0, 4, 4] },
                    ],
                    margin: [4, 4, 4, 4],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => '#000000',
              vLineColor: () => '#000000',
            },
            margin: [0, 0, 0, 0],
          },
          // Main Box 6: Terms & Conditions
          {
            table: {
              widths: ['100%'],
              body: [
                [
                  {
                    stack: [
                      { text: 'Terms & Conditions:', fontSize: 7.5, bold: true, margin: [0, 0, 0, 2] },
                      ...termsParagraphs,
                    ],
                    margin: [4, 4, 4, 4],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => '#000000',
              vLineColor: () => '#000000',
            },
            margin: [0, 0, 0, 0],
          },
          // Main Box 7: Footer Banner
          {
            table: {
              widths: ['100%'],
              body: [
                [
                  {
                    text: 'Thank You For Your Business!',
                    bold: true,
                    fontSize: 8.5,
                    alignment: 'center',
                    margin: [0, 3, 0, 3],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0.75,
              vLineWidth: () => 0.75,
              hLineColor: () => '#000000',
              vLineColor: () => '#000000',
            },
            margin: [0, 0, 0, 0],
          },
        );
      }
    });

    return {
      pageSize: isA5 ? 'A5' : 'A4',
      pageOrientation: isA5 ? 'landscape' : 'portrait',
      pageMargins: [18, 10, 18, 18],
      defaultStyle: {
        font: 'NotoSansTamil',
        fontSize: 8,
        color: '#000000',
      },
      content: docContent,
    };
  }
}
